import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  Signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { MonitoringActions } from '@states/monitoring/monitoring.action-types';
import { MonitoringSelectors } from '@states/monitoring/monitoring.selector-types';
import { features } from '@consts/text.const';
import { PreloaderColor } from '@enums/shared.enum';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createContactsTableConfig } from './contacts-table.util';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ContactDialogComponent } from './dialogs/contact-dialog/contact-dialog.component';
import { SharedActions } from '@states/shared/shared.action-types';
import { ConfirmDialogModel, UiButtonComponent, UiNoDataComponent } from '@ui';
import { ConfirmModalModel } from '../../../../services/confirm-dialogs/confirm-modal.model';
import { SharedEffects } from '@effects/shared.effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MonitoringModels } from '../../../monitoring/models/monitoring.models';
import { ofType } from '@ngrx/effects';
import { filter, map, take } from 'rxjs';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MenuActions } from "@states/menu/menu.action-types";
import { MenuKey } from "@enums/menu.enum";
import { RouteModalService } from '@ui';
import { openDialogByParams } from "@consts/open-dialog-by-params.consts";

type ViewState = 'INITIAL_LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-contacts',
  standalone: true,
  imports: [
    UiKitModule,
    TranslatePipe,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiDatasetTableComponent,
    ReactiveFormsModule,
    UiButtonComponent,
  ],
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactsComponent implements OnInit {
  public loaderColor = PreloaderColor;
  public noResults = features.contacts.noResults;
  public noData = features.contacts.noData;

  public selectAllOrgContacts = this.store$.selectSignal(
    MonitoringSelectors.selectAllOrgContacts,
  );

  public selectIsOrgContactsLoading: Signal<boolean> = this.store$.selectSignal(
    MonitoringSelectors.selectIsOrgContactsLoading,
  );

  public filters = new UntypedFormControl([]);

  protected search = syncQueryParam<string>('search');

  public readonly isFiltered = computed(() => !!this.search());

  public readonly filteredContacts = computed<MonitoringModels.OrganizationContact[]>(
    () => {
      const q = (this.search() ?? '').trim().toLowerCase();
      const list = (this.selectAllOrgContacts() ?? []) as MonitoringModels.OrganizationContact[];
      const sorted = (a: MonitoringModels.OrganizationContact, b: MonitoringModels.OrganizationContact) =>
        (b?.createdAt ?? 0) - (a?.createdAt ?? 0);
      if (!q) return [...list].sort(sorted);
      return list
        .filter((c) => (c?.name ?? '').toLowerCase().includes(q))
        .slice()
        .sort(sorted);
    },
  );

  public readonly tableConfig = computed(() => createContactsTableConfig());

  public readonly vm$ = computed(() => {
    const loading = this.selectIsOrgContactsLoading();
    const contacts = this.selectAllOrgContacts() ?? [];
    const isFiltered = this.isFiltered();
    const filteredContacts = this.filteredContacts();

    let viewState: ViewState;

    if (loading) {
      viewState = 'INITIAL_LOADING';
    } else if (isFiltered && !filteredContacts.length) {
      viewState = 'NO_RESULTS';
    } else if (!contacts.length) {
      viewState = 'INITIAL_EMPTY';
    } else {
      viewState = 'HAS_DATA';
    }

    return {
      loading,
      contacts,
      isFiltered,
      filteredContacts,
      viewState,
      search: this.search(),
    };
  });

  constructor(
    private store$: Store,
    private dialog: MatDialog,
    private sharedEffects$: SharedEffects,
    private destroyRef: DestroyRef,
    private translateService: TranslateService,
    private modalService: RouteModalService,
  ) {}

  ngOnInit() {
    this.store$.dispatch(MonitoringActions.getAllOrganizationContacts());
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.contacts }));

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) =>
            x.params['type'] ===
              ConfirmModalModel.Action.removeOrganizationContact &&
            !!x?.params['contactId'],
        ),
      )
      .subscribe((res) => {
        if (res.params['contactId']) {
          this.store$.dispatch(
            MonitoringActions.startDeleteOrgContact({
              contactId: res.params['contactId'],
            }),
          );
        }
      });

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.addOrganizationContact,
          open: () => this.openModal(),
        },
      ],
      this.destroyRef,
    );

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.editAccountContact,
          open: (params?: Record<string, any>) => this.openEditContactDialog(params),
          onClose: () => {
            this.modalService.removeQueryParam([
              'contactId',
              openDialogByParams.editAccountContact,
            ]);
          },
        },
      ],
      this.destroyRef,
    );
  }

  public onRowAction(event: {
    actionId: string;
    row: MonitoringModels.OrganizationContact;
  }): void {
    if (event.actionId === 'edit') {
      this.editContact(event.row);
    } else if (event.actionId === 'delete') {
      this.delete(event.row);
    }
  }

  public create(): void {
    this.modalService.openModal(openDialogByParams.addOrganizationContact);
  }

  public openModal(): MatDialogRef<
    ContactDialogComponent,
    void
  > {
    return this.dialog.open(ContactDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '538px',
      maxHeight: '90vh',
      disableClose: true,
      data: null,
    });
  }

  public editContact(row: MonitoringModels.OrganizationContact): void {
    this.modalService.openModal(openDialogByParams.editAccountContact, {
      contactId: String(row?._id ?? ''),
    });
  }

  public openEditContactDialog(params?: Record<string, any>) {
    const contactId = String(params?.['contactId'] ?? '');

    const openWith = (contact: MonitoringModels.OrganizationContact) =>
      this.dialog.open(ContactDialogComponent, {
        panelClass: 'modal-no-padding',
        width: '538px',
        maxHeight: '90vh',
        disableClose: true,
        data: { contact },
      });

    const cached = (this.selectAllOrgContacts() ?? []).find(
      (c) => String(c._id) === contactId,
    );
    if (contactId && cached) {
      return openWith(cached);
    }

    return this.store$.select(MonitoringSelectors.selectAllOrgContacts).pipe(
      filter(
        (list): list is MonitoringModels.OrganizationContact[] =>
          Array.isArray(list) &&
          list.some((c) => String(c._id) === contactId),
      ),
      take(1),
      map((list) => openWith(list.find((c) => String(c._id) === contactId)!)),
    );
  }

  private afterDialogClose(
    ref: MatDialogRef<ContactDialogComponent, boolean>,
  ): void {
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changed) => {
        if (changed) {
          this.store$.dispatch(MonitoringActions.getAllOrganizationContacts());
        }
      });
  }

  public delete(contact: MonitoringModels.OrganizationContact): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: 'CONFIRM_MODALS.ORGANIZATION_CONTACT_DELETE.MSG',
          title: this.translateService.instant(
            'CONFIRM_MODALS.ORGANIZATION_CONTACT_DELETE.TITLE',
            {
              name: contact.name,
            },
          ),
          confirm: 'CONFIRM_MODALS.COMMON_YES',
          disableClose: true,
          params: {
            contactId: String(contact._id),
            type: ConfirmModalModel.Action.removeOrganizationContact,
          },
        },
      }),
    );
  }
}
